import { useMemo, useState } from "react";
import { Clock, Search, X } from "lucide-react";

import BottomNav from "../components/BottomNav";
import EntryCard from "../components/EntryCard";
import EntryDetailSheet from "../components/EntryDetailSheet";
import EntryFormSheet from "../components/EntryFormSheet";
import { TYPE_ORDER, TYPE_STYLES } from "../utils/constants";
import { SHADOW, TOKENS as T } from "../styles/styles";

export default function SearchPage({
  entries,
  showForm,
  draft,
  currentUser,
  onDraftChange,
  onSaveEntry,
  onCancelEntry,
  onEditEntry,
  onRemoveEntry,
  onNavigate,
}) {
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedEntry, setSelectedEntry] = useState(null);
  // Real, in-session search history. Never seeded with fake terms.
  const [recentSearches, setRecentSearches] = useState([]);

  const normalizedQuery = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!normalizedQuery && selectedType === "all") return [];

    return entries.filter((entry) => {
      const title = (entry.title || "").toLowerCase();
      const note = (entry.note || "").toLowerCase();
      const tags = (entry.tags || []).join(" ").toLowerCase();
      const type = (entry.type || "").toLowerCase();
      const typeLabel = (TYPE_STYLES[entry.type]?.label || "").toLowerCase();

      const matchesQuery =
        normalizedQuery === "" ||
        title.includes(normalizedQuery) ||
        note.includes(normalizedQuery) ||
        tags.includes(normalizedQuery) ||
        type.includes(normalizedQuery) ||
        typeLabel.includes(normalizedQuery);

      const matchesType = selectedType === "all" || entry.type === selectedType;

      return matchesQuery && matchesType;
    });
  }, [entries, normalizedQuery, selectedType]);

  // Counts come straight from Firebase entries.
  const browseTypes = useMemo(
    () =>
      TYPE_ORDER.map((type) => ({
        type,
        label: TYPE_STYLES[type].label,
        Icon: TYPE_STYLES[type].icon,
        color: TYPE_STYLES[type].color,
        count: entries.filter((entry) => entry.type === type).length,
      })),
    [entries],
  );

  function rememberSearch(term) {
    const trimmed = term.trim();
    if (!trimmed) return;

    setRecentSearches((current) =>
      [trimmed, ...current.filter((item) => item !== trimmed)].slice(0, 5),
    );
  }

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      rememberSearch(query);
    }
  }

  function clearQuery() {
    setQuery("");
    setSelectedType("all");
  }

  const isBrowsing = normalizedQuery === "" && selectedType === "all";

  return (
    <div style={pageStyle}>
      <div style={headerShellStyle}>
        <h1 style={screenTitleStyle}>Search</h1>

        <div style={searchBarStyle}>
          <Search size={20} color={T.mutedForeground} style={{ flexShrink: 0 }} />

          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Titles, tags, notes…"
            style={searchInputStyle}
          />

          {(query || selectedType !== "all") && (
            <button type="button" onClick={clearQuery} style={clearButtonStyle}>
              <X size={16} color={T.mutedForeground} />
            </button>
          )}
        </div>
      </div>

      <div style={listShellStyle} className="scrollbar-hide">
        {isBrowsing ? (
          <>
            <p style={sectionLabelStyle}>Recent Searches</p>
            <div style={chipRowStyle}>
              {recentSearches.length > 0 ? (
                recentSearches.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => setQuery(term)}
                    style={recentChipStyle}
                  >
                    <Clock size={12} color={T.mutedForeground} />
                    {term}
                  </button>
                ))
              ) : (
                <p style={mutedNoteStyle}>Your recent searches will show here.</p>
              )}
            </div>

            <p style={{ ...sectionLabelStyle, marginTop: 24 }}>Browse by Type</p>
            <div style={browseGridStyle}>
              {browseTypes.map(({ type, label, Icon, color, count }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setSelectedType(type)}
                  style={browseCardStyle}
                >
                  <div
                    style={{
                      ...browseIconBoxStyle,
                      background: `${color}18`,
                    }}
                  >
                    <Icon size={20} color={color} />
                  </div>
                  <div>
                    <p style={browseLabelStyle}>{label}</p>
                    <p style={browseCountStyle}>
                      {count} {count === 1 ? "entry" : "entries"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : results.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={emptyIconBoxStyle}>
              <Search size={24} color={T.mutedForeground} />
            </div>
            <p style={emptyTitleStyle}>
              {query ? `Nothing matches “${query}”` : "Nothing here yet"}
            </p>
            <p style={emptySubtitleStyle}>
              {query
                ? "Try another word, or search a tag."
                : "Add something of this type and it'll show up here."}
            </p>
          </div>
        ) : (
          <>
            <p style={resultCountStyle}>
              {results.length} {results.length === 1 ? "result" : "results"}
            </p>

            {results.map((entry) => (
              <EntryCard
                key={entry.id}
                entry={entry}
                currentUser={currentUser}
                onTap={setSelectedEntry}
              />
            ))}
          </>
        )}
      </div>

      <BottomNav active="search" onNavigate={onNavigate} />

      <EntryDetailSheet
        entry={selectedEntry}
        currentUser={currentUser}
        onClose={() => setSelectedEntry(null)}
        onEditEntry={onEditEntry}
        onRemoveEntry={onRemoveEntry}
      />

      {showForm && (
        <EntryFormSheet
          draft={draft}
          onDraftChange={onDraftChange}
          onSaveEntry={onSaveEntry}
          onCancel={onCancelEntry}
        />
      )}
    </div>
  );
}

const pageStyle = {
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  background: T.background,
  color: T.foreground,
};

const headerShellStyle = {
  padding: "8px 20px 12px",
  flexShrink: 0,
};

const screenTitleStyle = {
  margin: "0 0 12px",
  fontSize: 26,
  fontWeight: 800,
  lineHeight: 1.15,
  color: T.foreground,
};

const searchBarStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  background: T.card,
  borderRadius: 16,
  padding: "14px 16px",
  boxShadow: SHADOW.card,
};

const searchInputStyle = {
  flex: 1,
  minWidth: 0,
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  fontFamily: "inherit",
  color: T.foreground,
};

const clearButtonStyle = {
  border: "none",
  background: "none",
  cursor: "pointer",
  padding: 0,
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
};

const listShellStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "8px 20px 140px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const sectionLabelStyle = {
  margin: "0 0 4px",
  fontSize: 10,
  fontWeight: 600,
  color: T.mutedForeground,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

const chipRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const recentChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  borderRadius: 999,
  border: "none",
  background: T.card,
  boxShadow: SHADOW.xs,
  color: T.foreground,
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "inherit",
  cursor: "pointer",
};

const mutedNoteStyle = {
  margin: 0,
  fontSize: 13,
  color: T.mutedForeground,
};

const browseGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};

const browseCardStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  textAlign: "left",
  background: T.card,
  borderRadius: 16,
  padding: 16,
  border: "none",
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "transform 0.15s ease",
};

const browseIconBoxStyle = {
  width: 40,
  height: 40,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const browseLabelStyle = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  color: T.foreground,
};

const browseCountStyle = {
  margin: "2px 0 0",
  fontSize: 11,
  color: T.mutedForeground,
};

const resultCountStyle = {
  margin: 0,
  fontSize: 12,
  color: T.mutedForeground,
};

const emptyStateStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: 176,
  textAlign: "center",
};

const emptyIconBoxStyle = {
  width: 56,
  height: 56,
  borderRadius: 24,
  background: T.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 12,
};

const emptyTitleStyle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: T.foreground,
};

const emptySubtitleStyle = {
  margin: "4px 0 0",
  fontSize: 13,
  color: T.mutedForeground,
};
